import { httpClient } from '@/utils/httpClient';
import { outputReport, getOutputFormat, getPreOpenedWindow } from '@/utils/reportOutput';
import { entityService } from '@/services/EntityService';
import {
  buildInventoryAwarenessReportHtml,
  type InventoryAwarenessItem,
  type InventoryAwarenessTemplateOptions,
} from '@/reports/templates/inventoryAwarenessTemplate';
import { splitLocationBreadcrumb } from '@/utils/locationHierarchy';

export const INVENTORY_AWARENESS_CATEGORY_OPTIONS = [
  'Out of Stock',
  'Exceeding Max Qty Level',
  'Below Min Qty Level',
  'Within Normal Range',
] as const;

type InventoryAwarenessCategory = (typeof INVENTORY_AWARENESS_CATEGORY_OPTIONS)[number];

interface InventoryAwarenessSummaryRow {
  id: string;
  asset_name: string;
  location_id: string | null;
  location_name: string | null;
  location_path: string | null;
  lin: string | null;
  nsn: string | null;
  stock_quantity: number;
  catalog_id: string | null;
}

interface OrderLineItemOutstandingRow {
  catalog_id: string | null;
  outstanding_quantity: number;
}

interface LocationHierarchyRow {
  id: string;
  breadcrumb: string | null;
}

const chunk = <T>(values: T[], size: number): T[][] => {
  if (size <= 0) return [values];
  const chunks: T[][] = [];
  for (let i = 0; i < values.length; i += size) {
    chunks.push(values.slice(i, i + size));
  }
  return chunks;
};

const parseNumber = (value: unknown): number | null => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value === 'string') {
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const coerceString = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

const isInventoryAwarenessCategory = (value: string): value is InventoryAwarenessCategory =>
  INVENTORY_AWARENESS_CATEGORY_OPTIONS.includes(value as InventoryAwarenessCategory);

const classifyInventoryAwarenessCategory = (
  onHand: number,
  minQty: number | null,
  maxQty: number | null,
): InventoryAwarenessCategory => {
  if (onHand === 0) {
    return 'Out of Stock';
  }
  if (typeof maxQty === 'number' && Number.isFinite(maxQty) && onHand > maxQty) {
    return 'Exceeding Max Qty Level';
  }
  if (typeof minQty === 'number' && Number.isFinite(minQty) && onHand < minQty) {
    return 'Below Min Qty Level';
  }
  return 'Within Normal Range';
};

const compareNullableText = (left: string | null | undefined, right: string | null | undefined) => {
  const leftValue = coerceString(left);
  const rightValue = coerceString(right);

  if (!leftValue && !rightValue) return 0;
  if (!leftValue) return 1;
  if (!rightValue) return -1;
  return leftValue.localeCompare(rightValue, undefined, { numeric: true, sensitivity: 'base' });
};

const sortInventoryAwarenessItems = (items: InventoryAwarenessItem[]): InventoryAwarenessItem[] =>
  items.sort((left, right) => {
    const linComparison = compareNullableText(left.lin, right.lin);
    if (linComparison !== 0) return linComparison;

    const nsnComparison = compareNullableText(left.nsn, right.nsn);
    if (nsnComparison !== 0) return nsnComparison;

    return left.nomenclature.localeCompare(right.nomenclature, undefined, {
      numeric: true,
      sensitivity: 'base',
    });
  });

export const runInventoryAwarenessReport = async (
  params: Record<string, unknown>,
): Promise<void> => {
  const rawAssetCategory = coerceString(params.assetCategory);
  const assetCategory =
    rawAssetCategory && isInventoryAwarenessCategory(rawAssetCategory) ? rawAssetCategory : null;

  const rows = await httpClient.get<InventoryAwarenessSummaryRow[]>(
    '/api/rest/data/v1/inventory_awareness_summary',
  );

  const catalogIds = Array.from(
    new Set(
      rows
        .map((row) => row.catalog_id)
        .filter((id): id is string => typeof id === 'string' && id.trim().length > 0),
    ),
  );

  const reorderThresholdByCatalogId = new Map<string, number | null>();
  const targetQuantityByCatalogId = new Map<string, number | null>();

  for (const batchIds of chunk(catalogIds, 75)) {
    const idFilter = batchIds.length === 1 ? `eq.${batchIds[0]}` : `in.(${batchIds.join(',')})`;

    const catalogs = await entityService.list({
      type: 'catalog',
      limit: batchIds.length,
      order: 'id.asc',
      filters: {
        id: idFilter,
        select: 'id,entity_data',
      },
    });

    for (const catalog of catalogs) {
      const entityData = (catalog.entity_data ?? {}) as Record<string, unknown>;
      reorderThresholdByCatalogId.set(catalog.id, parseNumber(entityData.reorder_threshold));
      targetQuantityByCatalogId.set(catalog.id, parseNumber(entityData.target_quantity));
    }
  }

  const outstandingQuery = new URLSearchParams({
    select: 'catalog_id,outstanding_quantity',
    outstanding_quantity: 'gt.0',
    limit: '10000',
  }).toString();
  const outstandingRows = await httpClient.get<OrderLineItemOutstandingRow[]>(
    `/api/rest/data/v1/order_line_items_view?${outstandingQuery}`,
  );

  const outstandingByCatalogId = new Map<string, number>();
  for (const row of outstandingRows) {
    const catalogId = row.catalog_id;
    if (!catalogId) continue;
    const outstanding = row.outstanding_quantity ?? 0;
    const current = outstandingByCatalogId.get(catalogId) ?? 0;
    outstandingByCatalogId.set(catalogId, current + outstanding);
  }

  const locationIds = Array.from(
    new Set(
      rows
        .map((row) => row.location_id)
        .filter((id): id is string => typeof id === 'string' && id.trim().length > 0),
    ),
  );

  const breadcrumbByLocationId = new Map<string, string>();
  for (const batchIds of chunk(locationIds, 75)) {
    const idFilter = batchIds.length === 1 ? `eq.${batchIds[0]}` : `in.(${batchIds.join(',')})`;
    const query = new URLSearchParams({
      select: 'id,breadcrumb',
      id: idFilter,
      limit: String(batchIds.length),
    }).toString();

    const locations = await httpClient.get<LocationHierarchyRow[]>(
      `/api/rest/data/v1/location_hierarchy?${query}`,
    );

    for (const location of locations) {
      if (location.breadcrumb) {
        breadcrumbByLocationId.set(location.id, location.breadcrumb);
      }
    }
  }

  const outOfStock: InventoryAwarenessItem[] = [];
  const exceedingMax: InventoryAwarenessItem[] = [];
  const belowMin: InventoryAwarenessItem[] = [];
  const withinNormal: InventoryAwarenessItem[] = [];

  for (const row of rows) {
    const onHand = row.stock_quantity ?? 0;
    const catalogId = row.catalog_id;

    const minQty = catalogId ? (reorderThresholdByCatalogId.get(catalogId) ?? null) : null;
    const maxQty = catalogId ? (targetQuantityByCatalogId.get(catalogId) ?? null) : null;
    const qtyOnOrder = catalogId ? (outstandingByCatalogId.get(catalogId) ?? 0) : null;
    const awarenessCategory = classifyInventoryAwarenessCategory(onHand, minQty, maxQty);

    if (assetCategory && awarenessCategory !== assetCategory) {
      continue;
    }

    const breadcrumb =
      (row.location_id ? breadcrumbByLocationId.get(row.location_id) : null) ??
      row.location_path ??
      row.location_name;
    const parts = breadcrumb ? splitLocationBreadcrumb(breadcrumb) : [];
    const site = parts[0] ?? row.location_name ?? null;
    const location = parts.length > 1 ? parts[1] : null;
    const sublocation = parts.length > 2 ? parts.slice(2).join(' / ') : null;

    const item: InventoryAwarenessItem = {
      lin: row.lin ?? null,
      nsn: row.nsn ?? null,
      nomenclature: row.asset_name,
      onHandQty: onHand,
      minQty,
      maxQty,
      qtyOnOrder,
      site,
      location,
      sublocation,
    };

    if (awarenessCategory === 'Out of Stock') {
      outOfStock.push(item);
    } else if (awarenessCategory === 'Exceeding Max Qty Level') {
      exceedingMax.push(item);
    } else if (awarenessCategory === 'Below Min Qty Level') {
      belowMin.push(item);
    } else {
      withinNormal.push(item);
    }
  }

  sortInventoryAwarenessItems(outOfStock);
  sortInventoryAwarenessItems(exceedingMax);
  sortInventoryAwarenessItems(belowMin);
  sortInventoryAwarenessItems(withinNormal);

  const options: InventoryAwarenessTemplateOptions = {
    generatedAt: new Date(),
    header: {
      assetCategory,
    },
    outOfStock,
    exceedingMax,
    belowMin,
    withinNormal,
  };

  const html = buildInventoryAwarenessReportHtml(options);
  outputReport(
    html,
    'inventory-awareness-report.html',
    getOutputFormat(params),
    getPreOpenedWindow(params),
  );
};
